// Optimistic-response scaffolding for UPDATE_ROUTE_STOPS. The document has
// eight root fields; an optimisticResponse must supply every one in the exact
// schema shape or the write is partial. Callers spread these empty roots and
// override the fields their flow actually changes.

import { gql } from '@apollo/client';
import { STOP_FIELDS } from '@/graphql/queries/stops';
import { TEMP_ROUTE } from '../constants';

// UPDATE_ROUTE_STOPS's update_routes_by_pk selects
// stopsByRouteId(order_by: {ordering: asc}), which writes the ORDERED
// storage key (read by QUERY_PLANNING_DATA). It does NOT write the
// argument-less stopsByRouteId storage key that OrderDetailsPage's
// cache-first ORDER_BY_ID reads — that key only self-heals via dangling refs,
// which inserts never create. This fragment writes the same authoritative
// stop set onto the argument-less key so both storage keys stay in sync.
const ROUTE_ARGLESS_STOPS_FRAGMENT = gql`
    ${STOP_FIELDS}

    fragment RouteArglessStops on routes {
        stopsByRouteId {
            ...StopFields
        }
    }
`;

export const emptyUpdateStopsRoots = () => ({
    delete_order_route_mappings: {
        __typename: 'order_route_mappings_mutation_response',
        affected_rows: 0,
        returning: [],
    },
    insert_order_wh_events: {
        __typename: 'order_wh_events_mutation_response',
        affected_rows: 0,
        returning: [],
    },
    update_orders_many: [],
    update_stops_many: [],
    insert_stops: {
        __typename: 'stops_mutation_response',
        affected_rows: 0,
        returning: [],
    },
    delete_stops: {
        __typename: 'stops_mutation_response',
        affected_rows: 0,
        returning: [],
    },
    insert_order_route_mappings: {
        __typename: 'order_route_mappings_mutation_response',
        affected_rows: 0,
        returning: [],
    },
});

/**
 * Build the optimistic UPDATE_ROUTE_STOPS response for the common
 * update-only flows (reorder, toggle, window edit).
 *
 * update_routes_by_pk resolves LAST in the document, so its nested
 * stopsByRouteId write wins the optimistic transaction — it must carry the
 * PATCHED stop set (`stops`), never the stale cached one, or it clobbers the
 * update_stops_many write that ran earlier in the same transaction. The
 * cached route is TEMP_ROUTE-filled so the write stays RouteFields-complete.
 *
 * @param {object} args
 * @param {object} args.route - full cached route object
 * @param {Array<object>} args.stops - the POST-mutation stop set (patched, StopFields-complete)
 * @param {object} [args.routeUpdate] - route _set fields (defaults to need_to_optimize: true)
 * @param {Array<object>} [args.stopsUpdated] - stops echoed in update_stops_many.returning
 * @param {Array<object>} [args.ordersUpdated] - orders echoed in update_orders_many.returning; need only be
 *     OrderDispatchScalars-complete (passing whole cached order rows is fine — the extra fields are
 *     outside the selection). These are the optimistic paint only; the real response overwrites them.
 * @returns {object} optimisticResponse for UPDATE_ROUTE_STOPS
 */
export const buildUpdateStopsOptimistic = ({
    route,
    stops,
    routeUpdate = { need_to_optimize: true },
    stopsUpdated = [],
    ordersUpdated = [],
}) => ({
    ...emptyUpdateStopsRoots(),
    ...(stopsUpdated.length
        ? {
              update_stops_many: [
                  {
                      __typename: 'stops_mutation_response',
                      affected_rows: stopsUpdated.length,
                      returning: stopsUpdated,
                  },
              ],
          }
        : {}),
    ...(ordersUpdated.length
        ? {
              update_orders_many: [
                  {
                      __typename: 'orders_mutation_response',
                      affected_rows: ordersUpdated.length,
                      returning: ordersUpdated,
                  },
              ],
          }
        : {}),
    update_routes_by_pk: {
        ...TEMP_ROUTE,
        ...route,
        ...routeUpdate,
        stopsByRouteId: stops,
        __typename: 'routes',
    },
});

/**
 * Build the optimistic UPDATE_STOP_SCALARS response. Covers exactly the
 * three roots of that document — update_orders_many, update_stops_many,
 * update_routes_by_pk — with no stopsByRouteId/orders rewrite, since the
 * scalar doc never selects them.
 *
 * @param {object} args
 * @param {object} args.route - full cached route object (TEMP_ROUTE-filled if unsure)
 * @param {object} [args.routeUpdate] - route _set fields (defaults to need_to_optimize: true)
 * @param {Array<object>} [args.stopsUpdated] - stops echoed in update_stops_many.returning; must be StopFields-complete
 * @param {Array<object>} [args.ordersUpdated] - orders echoed in update_orders_many.returning; must be OrderFields-complete
 * @returns {object} optimisticResponse for UPDATE_STOP_SCALARS
 */
export const buildStopScalarsOptimistic = ({
    route,
    routeUpdate = { need_to_optimize: true },
    stopsUpdated = [],
    ordersUpdated = [],
}) => ({
    update_orders_many: ordersUpdated.length
        ? [{ __typename: 'orders_mutation_response', affected_rows: ordersUpdated.length, returning: ordersUpdated }]
        : [],
    update_stops_many: stopsUpdated.length
        ? [{ __typename: 'stops_mutation_response', affected_rows: stopsUpdated.length, returning: stopsUpdated }]
        : [],
    update_routes_by_pk: { ...TEMP_ROUTE, ...route, ...routeUpdate, __typename: 'routes' },
});

/**
 * Shared UPDATE_ROUTE_STOPS cache update() for every caller. Evicts deleted
 * stops (invalidating the argument-less stopsByRouteId storage key so it
 * self-heals via a dangling-ref check), then writes update_routes_by_pk's
 * authoritative stopsByRouteId onto that same argument-less key so inserts
 * and reorders — which leave no dangling ref to heal — are reflected too.
 *
 * @param {import('@apollo/client').ApolloCache} cache
 * @param {object} data - UPDATE_ROUTE_STOPS mutation result data
 */
export const applyUpdateStopsResult = (cache, data) => {
    (data?.delete_stops?.returning || []).forEach((stop) => cache.evict({ id: cache.identify(stop) }));

    const route = data?.update_routes_by_pk;
    if (route?.stopsByRouteId) {
        cache.writeFragment({
            id: cache.identify({ __typename: 'routes', route_id: route.route_id }),
            fragment: ROUTE_ARGLESS_STOPS_FRAGMENT,
            fragmentName: 'RouteArglessStops',
            data: {
                __typename: 'routes',
                route_id: route.route_id,
                stopsByRouteId: route.stopsByRouteId,
            },
        });
    }
};

/**
 * UPDATE_ROUTE_STOPS update() for callers that move stops.
 *
 * Order scalars need no handling here: update_orders_many returns
 * OrderDispatchScalars, so the response normalizes them onto the order
 * entities before this runs — for both the optimistic and the real pass, with
 * the real one overwriting the optimistic layer. Patching them from the
 * `$order_updates` `_set` instead would make the client's request, not the
 * server's answer, the last writer.
 */
export const updateStopsCacheHandler = (cache, { data } = {}) => {
    applyUpdateStopsResult(cache, data);
};
